// frontend/src/components/MemberEditorModal.jsx

// React y librerías de terceros
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n';

// Utilidades y funciones helper
import { formatDateForInput } from '../utils/dateUtils';
import CountrySelect from './fields/CountrySelect';

import {
  shouldShowAgeWarning,
  shouldShowInvalidBirthdate,
  shouldShowInvalidBanner
} from '../utils/memberValidationUtils';

// Hooks y manejadores personalizados
import { fetchPersonData } from '../hooks/useFetchPersonData';
import { useMemberValidation } from '../hooks/useMemberValidation';
import { useNationalIdLengthChecker } from '../hooks/useNationalIdLengthChecker';
import { useValidationConfig } from '../hooks/useValidationConfig';
import { handleBirthdateBlur as birthdateBlurHandler } from '../hooks/handleBirthdateBlur';
import {
  handleNationalIdBlur as nationalIdBlurHandler,
  handleNationalIdChange as nationalIdChangeHandler
} from '../hooks/useNationalIdHandlers';

// Debug: habilita logs de gating del botón "Create/Save"
const DEBUG_FORM_GATING = false;

export default function MemberEditorModal({
  mode = 'edit',
  editedMember,
  onChange,
  onSave,
  onClose,
  errorMessage = '',
  errorName = '',
  errorCode = ''
}) {
  const { _, lang } = useTranslation()

  // Estados para control de validación
  const [nationalIdTouched, setNationalIdTouched] = useState(false)
  const [isNationalIdFocused, setIsNationalIdFocused] = useState(false)
  const [wasChecksumInvalid, setWasChecksumInvalid] = useState(false)
  const [config, setConfig] = useState(null)
  const [isNationalIdValid, setIsNationalIdValid] = useState(false)
  const [isFieldsDisabled, setIsFieldsDisabled] = useState(false)
  const [formValid, setFormValid] = useState(false)

  const isEditMode = mode === 'edit';
  const isVerifiedByRegistry =
    isEditMode && editedMember.validation_source?.startsWith('PADRON_');

  // Helpers locales
  const trim = v => (v || '').trim()

  // Detecta validación (compat: status 'valid', 'VERIFIED' o fuente PADRON_)
  const isMemberValidated = (m = {}) => {
    const vs = String(m.validation_status || m.person_validation_status || '').toLowerCase();
    const src = String(m.validation_source || m.person_validation_source || '');
    return (vs === 'valid') || (m.validation_status === 'VERIFIED') || src.startsWith('PADRON_');
  };

  // Bloqueo de campos en modo edición si está validado (solo se deben poder editar contact/passport)
  useEffect(() => {
    const lock = mode === 'edit' && isMemberValidated(editedMember);
    setIsFieldsDisabled(lock);
  }, [
    mode,
    editedMember?.validation_status,
    editedMember?.validation_source,
    editedMember?.person_validation_status,
    editedMember?.person_validation_source
  ]);

  // Rango de fechas (hoy y hoy-80y) formateado a YYYY-MM-DD sin zona
  const pad2 = n => String(n).padStart(2, '0');
  const fmtYMD = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const today = new Date();
  const maxBirthStr = fmtYMD(today);            // No permitir fechas futuras

  const minBirthDate = new Date(today);
  minBirthDate.setFullYear(minBirthDate.getFullYear() - 80);
  const minBirthStr = fmtYMD(minBirthDate);     // Incluye 80 años exactos

  // --- Locale visual del date picker ---
  // Ajuste según política: 'en-US' (mes/día/año) o 'en-GB' (día/mes/año).
  // Aquí usamos 'es-DO' si la app está en español; 'en-US' si está en inglés.
  const dateLang = (lang && lang.startsWith('es')) ? 'es-DO' : 'en-US';
  // Para formato europeo en inglés, usar:
  // const dateLang = (lang && lang.startsWith('es')) ? 'es-DO' : 'en-GB';

  // Verifica si ya se seleccionó nacionalidad
  const isNationalitySelected = !!trim(editedMember.nationality)

  // Carga config del país según nacionalidad
  useValidationConfig(
    trim(editedMember.nationality) || null,
    setConfig
  )

  // Limpia la cédula automáticamente si cambia la nacionalidad
  const prevNatRef = useRef(editedMember.nationality)

  useEffect(() => {
    const prevNat = prevNatRef.current
    const currNat = editedMember.nationality

    if (prevNat && currNat && prevNat !== currNat) {
      const validated = isMemberValidated(editedMember)
      if (!validated) {
        // Blanqueo amplio (según regla): docs, nombres, contacto, fecha y género
        const toClear = [
          'nationalId','passport','firstName','middleName','lastName','secondLastName',
          'gender','birthdate','contact'
        ];
        toClear.forEach(f =>
          onChange({ target: { name: f, value: '' } })
        );
      } else {
        // Si está validado, no blanqueamos nada
      }
      setNationalIdTouched(false)
      setWasChecksumInvalid(false)
      // El lock (si aplica) lo gobierna el efecto de arriba
    }

    prevNatRef.current = currNat
  }, [editedMember.nationality])

  // Valida largo / patrón del ID según config (actualiza isNationalIdValid)
  useNationalIdLengthChecker(editedMember, setIsNationalIdValid)

  // ---- Normalizador de fechas con "/" → ISO YYYY-MM-DD ----
  const toISOFromSlash = (raw, preferDMY = true) => {
    if (!raw) return ''
    const s = String(raw).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s

    const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
    if (!m) return ''

    let a = parseInt(m[1], 10) // primer número
    let b = parseInt(m[2], 10) // segundo número
    const y = parseInt(m[3], 10)

    // Heurística: si uno >12, es el día
    let d, M
    if (a > 12 && b <= 12) { d = a; M = b }
    else if (b > 12 && a <= 12) { d = b; M = a }
    else if (preferDMY) { d = a; M = b }
    else { M = a; d = b }

    if (M < 1 || M > 12 || d < 1 || d > 31) return ''

    const dt = new Date(y, M - 1, d)
    if (
      dt.getFullYear() !== y ||
      dt.getMonth() !== M - 1 ||
      dt.getDate() !== d
    ) return ''

    return `${y}-${pad2(M)}-${pad2(d)}`
  }

  // Normaliza automáticamente la fecha del padrón a ISO si viene con "/"
  useEffect(() => {
    const raw = (editedMember.birthdate || '').trim()
    if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) return

    // Preferencia: español → DMY; inglés → MDY (ajusta si quieres)
    const preferDMY = (lang && lang.startsWith('es')) ? true : false
    const iso = toISOFromSlash(raw, preferDMY)
    if (iso && iso !== raw) {
      onChange({ target: { name: 'birthdate', value: iso } })
    }
  }, [editedMember.birthdate, lang, onChange])

  // Cálculo de validez del formulario según reglas acordadas
  const { isFormValid } = useMemberValidation(
    editedMember,
    config,
    isFieldsDisabled
  );

  // Se actualiza el estado del formulario basado en el resultado del hook.
  useEffect(() => {
    setFormValid(isFormValid);
  }, [isFormValid]);

  // Maneja blur en fecha
  const handleBirthdateBlur = e => birthdateBlurHandler(e, onChange)

  // Maneja blur en cédula (puede consultar padrón/ checksum)
  const handleNationalIdBlur = e =>
    nationalIdBlurHandler(
      e,
      config,
      onChange,
      // Pasamos la nacionalidad desde el estado del miembro.
      fetchPersonData,
      setIsFieldsDisabled
    )

  // Maneja cambios en campo de cédula (marca wasChecksumInvalid si aplica)
  const handleNationalIdChange = e =>
    nationalIdChangeHandler(
      e,
      config,
      onChange,
      setNationalIdTouched,
      setWasChecksumInvalid
    )

  const isCreateMode = mode === 'create'
  const hasName = editedMember.firstName || editedMember.lastName

  return (
    <div className="modal-backdrop">
      <div className="modal-box w-full max-w-2xl overflow-y-auto max-h-[80vh]">

        {/* Autofill trap (absorbe username/password de Chrome/Safari) */}
        <input
          type="text"
          name="fake_user"
          autoComplete="username"
          style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
          tabIndex={-1}
        />
        <input
          type="password"
          name="fake_pass"
          autoComplete="new-password"
          style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
          tabIndex={-1}
        />

        <h2 className="modal-title mb-4">
          {isCreateMode
            ? _('Add Member')
            : hasName
              ? `${editedMember.firstName || ''} ${editedMember.lastName || ''}`
              : _('Edit Member')}
        </h2>

        {/* Banner de bloqueo por validación */}
        {isFieldsDisabled && isEditMode && (
          <div className="banner-warning">
            ★ {_('Verified member — Editing is restricted (only Contact & Passport).')}
          </div>
        )}

        {/* Sección de botones de acción en la parte superior */}
      <div className="form-actions mt-2 mb-4 md:hidden">
        <button className="button-secondary" onClick={onClose}>
          {_('Cancel')}
        </button>
        <button
          className="button-primary"
          onClick={onSave}
          disabled={!formValid}
        >
          {mode === 'create' ? _('Create') : _('Save')}
        </button>
      </div>

        {/* Banner si el ID es inválido y se requiere validación */}
        {isNationalIdFocused &&
          config?.nationalIdLength &&
          (config?.validateAgainstPadron || config?.checksum) &&
          shouldShowInvalidBanner(editedMember.nationalId) && (
            <div className="banner-warning">
              {_(config.message)}
            </div>
          )}

        <div className="flex flex-col gap-y-4">
          {/* Sección: Nacionalidad y Cédula */}
          <div className="flex flex-col gap-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                <label className="form-label">{_('Nationality')}</label>
                <CountrySelect
                  name="nationality"
                  value={editedMember.nationality || ''}
                  onChange={onChange}
                  disabled={isEditMode}
                  className="input-inline w-full"
                />
              </div>

              <div>
                <label className="form-label">{_('National ID')}</label>
                <input
                  type="text"
                  name="nationalId"
                  className={`input-inline w-full ${errorCode === 'E_DUP_NATIONAL_ID' ? 'input-error' : ''}`}
                  value={editedMember.nationalId || ''}
                  onChange={handleNationalIdChange}
                  onFocus={() => setIsNationalIdFocused(true)}
                  onBlur={handleNationalIdBlur}
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  inputMode="numeric"
                />
                {errorCode === 'E_DUP_NATIONAL_ID' && (
                  <div className="text-sm text-red-600 mt-1">
                    {_('This National ID already exists for this country.')}
                  </div>
                )}
              </div>
            </div>

            {/* Sección: Pasaporte y Contacto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{_('Passport')}</label>
                <input
                  type="text"
                  name="passport"
                  className={`input-inline w-full ${errorCode === 'E_DUP_PASSPORT' ? 'input-error' : ''}`}
                  value={editedMember.passport || ''}
                  onChange={onChange}
                  disabled={!isNationalitySelected}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                {errorCode === 'E_DUP_PASSPORT' && (
                  <div className="text-sm text-red-600 mt-1">
                    {_('This passport already exists for this country.')}
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">{_('Contact')}</label>
                <input
                  type="text"
                  name="contact"
                  className="input-inline w-full"
                  value={editedMember.contact || ''}
                  onChange={onChange}
                  disabled={!isNationalitySelected}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Sección: Nombres y Apellidos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{_('First Name')}</label>
                <input
                  type="text"
                  name="firstName"
                  className="input-inline w-full"
                  value={editedMember.firstName || ''}
                  onChange={onChange}
                  required
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>

              <div>
                <label className="form-label">{_('Middle Name')}</label>
                <input
                  type="text"
                  name="middleName"
                  className="input-inline w-full"
                  value={editedMember.middleName || ''}
                  onChange={onChange}
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{_('Last Name')}</label>
                <input
                  type="text"
                  name="lastName"
                  className="input-inline w-full"
                  value={editedMember.lastName || ''}
                  onChange={onChange}
                  required
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>

              <div>
                <label className="form-label">{_('Second Last Name')}</label>
                <input
                  type="text"
                  name="secondLastName"
                  className="input-inline w-full"
                  value={editedMember.secondLastName || ''}
                  onChange={onChange}
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Sección: Fecha de nacimiento y género */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">{_('Birthdate')}</label>
                <input
                  type="date"
                  name="birthdate"
                  className="input-inline w-full"
                  value={formatDateForInput(editedMember.birthdate || '')}
                  onChange={onChange}
                  onBlur={handleBirthdateBlur}
                  required
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  lang={dateLang}        // UI del calendario localizada
                  min={minBirthStr}      // No más de 80 años hacia atrás
                  max={maxBirthStr}      // No permitir fechas futuras
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                {shouldShowAgeWarning(editedMember.birthdate) && (
                  <div className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
                    {_('This age exceeds contractual policies.')}
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">{_('Gender')}</label>
                <select
                  name="gender"
                  className="input-inline w-full"
                  value={editedMember.gender || ''}
                  onChange={onChange}
                  disabled={!isNationalitySelected || isFieldsDisabled || isVerifiedByRegistry}
                  autoComplete="off"
                >
                  <option value="">—</option>
                  <option value="M">{_('Male')}</option>
                  <option value="F">{_('Female')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones del formulario */}
        <div className="form-actions">
          <button className="button-secondary" onClick={onClose}>
            {_('Cancel')}
          </button>
          <button
            className="button-primary"
            onClick={onSave}
            disabled={!formValid}
          >
            {mode === 'create' ? _('Create') : _('Save')}
          </button>
        </div>

        {errorMessage && (
          <div className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
            <p>{_(errorMessage)}</p>
            {errorName && (
              <p className="font-semibold">{errorName}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
